// ─────────────────────────────────────────────────────────────────────────────
// pop_dialog.ts — Animated hand-drawn pop-up dialog
//
// Appears as hand-scrawled traces on the notebook page. No fill background,
// only a frame border. Anchored at a corner (bottom-left or bottom-right)
// positioned at the tap point.
//
// Selection backend is shared with every other interactor surface in the
// app: a single `SelectionWidget` powers the analysis (proximity / tap)
// and per-kind animations (button press, checkbox tick, swatch pulse,
// icon glow). The dialog only adds two specific behaviours on top of
// that: per-sub-cell hit testing for `inline-toggles` and retap-to-
// untoggle for `isToggle` options.
//
// Animation sequence at open time:
//   1. Frame: corner nodes shine, lines trace CW/CCW to next node
//   2. Content: text elements appear one-by-one, each shines then cools
//   3. Selections fire kind-specific animations on user input
//
// Modes:
//   - Transient (original): resolves after maxStrokes, then disappears
//   - Static: stays open until explicitly dismissed; unlimited strokes
// ─────────────────────────────────────────────────────────────────────────────

import {
  type ShineParams, type FrameAnimState,
  DEFAULT_SHINE, LACRE_SHINE,
  createFrameAnim, evalFramePhases,
} from './drawn_ui';
import {
  SelectionWidget, type WidgetItem, type GestureKind, type ItemKind,
} from './selection_widget';
import { SelectionContainer, type ContainerStrokeResult } from './widget_container';
import { Polygon, type PolyNode, type PolyEdge, type PolygonSnapshot } from './polygon';

// Re-export SelectionItem as alias for backward compat
export type SelectionItem = WidgetItem;

/** Map a PopOption's display flavour to the SelectionWidget's animation kind. */
function kindForOption(opt: PopOption): ItemKind {
  if (opt.isToggle) return 'checkbox';
  if (opt.display === 'inline-toggles') return 'toggle';
  if (opt.display === 'swatch') return 'swatch';
  if (opt.display === 'icon-only') return 'icon';
  return 'button';
}

// ── Public types ─────────────────────────────────────────────────────────────

export interface PopOption {
  label: string;
  group: number;
  value: unknown;
  /** Optional icon (single character) rendered before label. */
  icon?: string;
  /** If true, this option is rendered as a toggle checkbox. */
  isToggle?: boolean;
  /** Current toggle state (only meaningful when isToggle is true). */
  toggled?: boolean;
  /** Visual size hint: 'normal' | 'icon-only' | 'swatch' | 'inline-toggles'. */
  display?: 'normal' | 'icon-only' | 'swatch' | 'inline-toggles';
  /** For swatch display: fill color. */
  swatchColor?: string;
  /** For size display: circle radius to draw.  0 = text-only grid cell. */
  swatchRadius?: number;
  /** For inline-toggles display: sub-toggle entries [{label, key, toggled}]. */
  subToggles?: { label: string; key: string; toggled: boolean }[];
  /** Explicit layout row (0-based). Groups sharing a row are placed side by side. */
  row?: number;
}

export type AnchorCorner = 'bottom-left' | 'bottom-right' | 'top-left' | 'top-right';

export interface PopDialogConfig {
  /** Anchor point (page-local) — the corner of the dialog. */
  anchorX: number;
  anchorY: number;
  anchor: AnchorCorner;
  options: PopOption[];
  /** Max strokes before auto-resolve.  0/Infinity = static (no auto-resolve). */
  maxStrokes: number;
  /** Pre-selected values: group → option index. */
  preSelected?: Map<number, number>;
  /** Fired instantly each time a selection changes (before resolve). */
  onSelect?: (group: number, option: PopOption) => void;
  /** If true, outside taps do NOT dismiss — only the close X does. */
  persistent?: boolean;
  /** Page dimensions for bounds clamping. */
  pageWidth?: number;
  pageHeight?: number;
  onResolve: (selections: Map<number, PopOption>) => void;
  onDismiss: () => void;
}

// ── Backward compat: convert old { cx, cy } configs ──────────────────────────

export interface PopDialogConfigLegacy {
  cx: number;
  cy: number;
  options: PopOption[];
  maxStrokes: number;
  preSelected?: Map<number, number>;
  onResolve: (selections: Map<number, PopOption>) => void;
  onDismiss: () => void;
}

function normaliseConfig(cfg: PopDialogConfig | PopDialogConfigLegacy): PopDialogConfig {
  if ('anchorX' in cfg) return cfg;
  return {
    anchorX: cfg.cx,
    anchorY: cfg.cy,
    anchor: 'bottom-left',
    options: cfg.options,
    maxStrokes: cfg.maxStrokes,
    preSelected: cfg.preSelected,
    onSelect: undefined,
    persistent: false,
    onResolve: cfg.onResolve,
    onDismiss: cfg.onDismiss,
  };
}

// ── Internal records ─────────────────────────────────────────────────────────

interface OptionLayout {
  x: number; y: number;        // top-left of the option cell
  w: number; h: number;
  wobble: number;               // slight rotation for hand-scrawled feel
  idx: number;                  // index into config.options
  revealDelay: number;          // delay before this element appears (ms)
}

// ── Layer ────────────────────────────────────────────────────────────────────

export class PopDialogLayer extends SelectionContainer {
  private _cfg: PopDialogConfig | null = null;
  private _layouts: OptionLayout[] = [];
  private _bbox = { x: 0, y: 0, w: 0, h: 0 };
  private _selected = new Map<number, number>();   // group → option index
  private _selectionWidget = new SelectionWidget();
  private _strokes = 0;
  private _frameAnim: FrameAnimState | null = null;
  private _framePoly: Polygon | null = null;
  private _dragStart: { x: number; y: number; bx: number; by: number } | null = null;
  /** Y-coordinates (page-local) at the midpoint between consecutive layout rows. */
  private _separatorYs: number[] = [];

  constructor() {
    super();
    this.drawAllowed = false;  // pop dialogs protect their area
    this.priority = 20;       // highest priority — checked first
  }

  override get active(): boolean { return this._cfg !== null; }
  get persistent(): boolean { return this._cfg?.persistent === true; }
  get dragging(): boolean { return this._dragStart !== null; }
  get dialogBbox(): { x: number; y: number; w: number; h: number } { return this._bbox; }

  // ── SelectionContainer interface ──────────────────────────────────────

  strokeCrossesAny(pts: Array<{ x: number; y: number }>): boolean {
    if (!this._cfg) return false;
    const b = this._bbox;
    const m = 8;
    for (const p of pts) {
      if (p.x >= b.x - m && p.x <= b.x + b.w + m &&
          p.y >= b.y - m && p.y <= b.y + b.h + m) {
        return true;
      }
    }
    return false;
  }

  analyzeContainerStroke(pts: Array<{ x: number; y: number }>): ContainerStrokeResult | null {
    if (!this._cfg) return null;
    if (!this.strokeCrossesAny(pts)) return null;
    const result = this.analyzeExternalStroke(pts);
    return {
      selection: result ? {
        item: this._selectionWidget.items.find(it => it.value === result.option.value) ?? this._selectionWidget.items[0],
        gesture: result.gesture,
      } : null,
      widget: this._selectionWidget,
      container: this,
      dissolve: !this.drawAllowed && result === null,
    };
  }

  // ── Lifecycle ──────────────────────────────────────────────────────────

  open(cfg: PopDialogConfig | PopDialogConfigLegacy): void {
    this._cfg = normaliseConfig(cfg);
    this._selected.clear();
    this._selectionWidget.clear();
    this._strokes = 0;
    this._closingStart = null;
    this._dragStart = null;
    this._frameAnim = createFrameAnim();
    this._buildLayout();
    this._buildFramePoly();
    this._syncSelectionWidgetItems();

    // Pre-selected values → spawn initial wonky circles
    if (this._cfg.preSelected) {
      for (const [group, idx] of this._cfg.preSelected) {
        this._selected.set(group, idx);
        this._selectionWidget.preSelect(group, idx);
      }
    }
  }

  /**
   * Single-call hit resolution for pointer-down.
   * Returns 'close', 'drag', 'body', or 'miss'.
   */
  resolveHit(x: number, y: number): 'close' | 'drag' | 'body' | 'miss' {
    if (!this._cfg) return 'miss';
    const b = this._bbox;
    // Close X — top-right corner
    if (Math.abs(x - (b.x + b.w - 10)) < 12 && Math.abs(y - (b.y + 10)) < 12) return 'close';
    // Drag handle — top-left corner
    if (Math.abs(x - (b.x + 10)) < 14 && Math.abs(y - (b.y + 10)) < 14) return 'drag';
    // Body — expanded margin
    const m = 28;
    if (x >= b.x - m && x <= b.x + b.w + m && y >= b.y - m && y <= b.y + b.h + m) return 'body';
    return 'miss';
  }

  /** Is (x, y) inside the dialog bounding box? (page-local coords) */
  hitTest(x: number, y: number): boolean {
    if (!this._cfg) return false;
    const m = 28;
    const b = this._bbox;
    return x >= b.x - m && x <= b.x + b.w + m &&
           y >= b.y - m && y <= b.y + b.h + m;
  }

  /** Is (x, y) over the close X button? (inside top-right corner) */
  hitTestClose(x: number, y: number): boolean {
    if (!this._cfg) return false;
    const b = this._bbox;
    const cx = b.x + b.w - 10;
    const cy = b.y + 10;
    return Math.abs(x - cx) < 12 && Math.abs(y - cy) < 12;
  }

  /** Is (x, y) over the drag handle? (top-left corner, handler bar) */
  hitTestDrag(x: number, y: number): boolean {
    if (!this._cfg) return false;
    const b = this._bbox;
    const hx = b.x + 10;
    const hy = b.y + 10;
    return Math.abs(x - hx) < 14 && Math.abs(y - hy) < 14;
  }

  /** Begin dragging the dialog. */
  beginDrag(x: number, y: number): void {
    this._dragStart = { x, y, bx: this._bbox.x, by: this._bbox.y };
  }

  /** Update drag position — moves the entire dialog. */
  moveDrag(x: number, y: number): void {
    if (!this._dragStart || !this._cfg) return;
    const dx = x - this._dragStart.x;
    const dy = y - this._dragStart.y;
    const newBx = this._dragStart.bx + dx;
    const newBy = this._dragStart.by + dy;
    // Shift all layouts by the delta from old bbox
    const shiftX = newBx - this._bbox.x;
    const shiftY = newBy - this._bbox.y;
    for (const lay of this._layouts) {
      lay.x += shiftX;
      lay.y += shiftY;
    }
    // Re-sync selection widget items (positions shifted)
    this._syncSelectionWidgetItems();
    // Re-apply existing selections so wonky rings follow
    for (const [group, idx] of this._selected) {
      this._selectionWidget.preSelect(group, idx);
    }
    this._bbox.x = newBx;
    this._bbox.y = newBy;
    // Shift separator Ys
    for (let i = 0; i < this._separatorYs.length; i++) {
      this._separatorYs[i] += shiftY;
    }
    this._updateFramePolyNodes();
  }

  /** End drag. */
  endDrag(): void { this._dragStart = null; }

  // Stroke capture lives in canvas_app — it commits the stroke to the ink
  // layer, then calls `analyzeExternalStroke` (below) so the dialog can
  // run its own hit test against the same SelectionWidget every other
  // interactor surface uses. There's no internal `beginStroke/endStroke`
  // path any more.

  /**
   * Analyse an externally-committed stroke for selection. Called by
   * canvas_app on pen-up when the stroke touches the dialog bbox. Returns
   * the selected option, or null if no item was hit (caller fades the
   * stroke either way). `isLace` is retained on the return purely for
   * legacy callers; it's always false now since circle-gating is gone.
   */
  analyzeExternalStroke(pts: { x: number; y: number }[]): { option: PopOption; isLace: boolean; gesture: GestureKind } | null {
    if (!this._cfg || pts.length < 1) return null;

    // Stroke must intersect the dialog bbox (with a small slop margin).
    const b = this._bbox;
    const m = 8;
    let crossesDialog = false;
    for (const p of pts) {
      if (p.x >= b.x - m && p.x <= b.x + b.w + m &&
          p.y >= b.y - m && p.y <= b.y + b.h + m) {
        crossesDialog = true;
        break;
      }
    }
    if (!crossesDialog) return null;

    const widgetResult = this._selectionWidget.analyzeStroke(pts);
    if (!widgetResult) return null;

    // Find the option whose layout cell is closest to the widget's hit.
    const sCx = widgetResult.item.cx;
    const sCy = widgetResult.item.cy;
    let bestIdx = -1;
    let bestDist = Infinity;
    for (const lay of this._layouts) {
      const optCx = lay.x + lay.w / 2;
      const optCy = lay.y + lay.h / 2;
      const dist = Math.sqrt((sCx - optCx) ** 2 + (sCy - optCy) ** 2);
      if (dist < bestDist) {
        bestDist = dist;
        bestIdx = lay.idx;
      }
    }
    if (bestIdx < 0) return null;

    const opt = this._cfg.options[bestIdx];
    const lay = this._layouts[bestIdx];

    // Per-kind dispatch — checkbox retap deselects, inline-toggle finds
    // the specific sub-cell, everything else goes through `_selectOption`
    // for the normal radio-group selection animation.
    if (opt.display === 'inline-toggles' && opt.subToggles) {
      // Map the stroke centroid's x into a sub-toggle slot (24px stride).
      let sx = 0;
      for (const p of pts) sx += p.x;
      const cx = sx / pts.length;
      const localX = cx - lay.x;
      let tx = 2;
      let flipped = false;
      for (const sub of opt.subToggles) {
        if (localX >= tx && localX <= tx + 24) {
          sub.toggled = !sub.toggled;
          flipped = true;
          this._cfg.onSelect?.(opt.group, opt);
          break;
        }
        tx += 24;
      }
      if (!flipped && opt.subToggles.length > 0) {
        opt.subToggles[0].toggled = !opt.subToggles[0].toggled;
        this._cfg.onSelect?.(opt.group, opt);
      }
      // Still fire the per-kind animation on the cell as a whole.
      this._selectionWidget.applySelection(
        widgetResult.item, widgetResult.gesture, pts,
      );
    } else if (opt.isToggle && this._selected.get(opt.group) === bestIdx) {
      // Retap on the currently-toggled checkbox → untoggle.
      opt.toggled = false;
      this._selected.delete(opt.group);
      this._selectionWidget.clear();
      for (const [g, i] of this._selected) this._selectionWidget.preSelect(g, i);
      this._cfg.onSelect?.(opt.group, opt);
    } else {
      if (opt.isToggle) opt.toggled = true;
      this._selectOption(bestIdx, opt, pts);
    }

    this._strokes++;
    if (this._cfg.maxStrokes > 0 && this._strokes >= this._cfg.maxStrokes) {
      this._resolve();
    }
    return { option: opt, isLace: false, gesture: widgetResult.gesture };
  }

  private _selectOption(
    idx: number, opt: PopOption,
    strokePts?: Array<{ x: number; y: number }>,
  ): void {
    if (!this._cfg) return;
    const lay = this._layouts[idx];
    this._selected.set(opt.group, idx);
    const item: WidgetItem = {
      cx: lay.x + lay.w / 2,
      cy: lay.y + lay.h / 2,
      r: Math.min(lay.w, lay.h) / 2 - 2,
      group: opt.group,
      value: opt.value,
      kind: kindForOption(opt),
    };
    this._selectionWidget.applySelection(item, 'over', strokePts);
    this._cfg.onSelect?.(opt.group, opt);
  }

  private _closingStart: number | null = null;
  private _closingDuration = 400; // ms for reverse animation

  // ── Dismiss ────────────────────────────────────────────────────────────

  /** Begin orderly close animation (reverse of assembly). */
  dismiss(): void {
    if (!this._cfg) return;
    // Start closing animation
    if (this._closingStart === null) {
      this._closingStart = performance.now();
    }
  }

  /** Instant dismiss without animation. */
  private _forceClose(): void {
    if (!this._cfg) return;
    const cb = this._cfg.onDismiss;
    this._cfg = null;
    this._frameAnim = null;
    this._framePoly = null;
    this._closingStart = null;
    cb();
  }

  // ── Render ─────────────────────────────────────────────────────────────

  draw(ctx: CanvasRenderingContext2D, ox: number, oy: number): void {
    if (!this._cfg || !this._frameAnim) return;

    const now = performance.now();

    // Tick the polygon frame
    if (this._framePoly) this._framePoly.tick(now);

    // Handle closing animation
    if (this._closingStart !== null) {
      const closeElapsed = now - this._closingStart;
      if (closeElapsed >= this._closingDuration) {
        this._forceClose();
        return;
      }
      const closeT = 1 - closeElapsed / this._closingDuration;

      ctx.save();
      ctx.globalAlpha = closeT;

      const b = this._bbox;
      const cx = ox + b.x + b.w / 2;
      const cy = oy + b.y + b.h / 2;
      ctx.translate(cx, cy);
      ctx.scale(0.5 + closeT * 0.5, 0.5 + closeT * 0.5);
      ctx.translate(-cx, -cy);

      // Draw polygon frame while closing
      if (this._framePoly) {
        this._drawPolyFrame(ctx, ox, oy);
      }

      ctx.restore();
      return;
    }

    const phases = evalFramePhases(this._frameAnim, now);
    if (phases.visible <= 0) return;

    ctx.save();

    // 1a. Paper-style fill behind the frame so the dialog reads on
    // top of arbitrary page content. Slightly inset rounded rect in
    // the page's parchment colour, faded in with the frame trace.
    if (this._framePoly && phases.frameT > 0) {
      const b = this._bbox;
      const fillAlpha = Math.min(1, phases.frameT * 2) * 0.92;
      const PAD = 4;
      const RAD = 6;
      const fx = ox + b.x - PAD;
      const fy = oy + b.y - PAD;
      const fw = b.w + PAD * 2;
      const fh = b.h + PAD * 2;
      ctx.save();
      ctx.globalAlpha = fillAlpha;
      // Slightly whiter than the page parchment so the dialog reads
      // as a paper card laid on top, not a tinted overlay.
      ctx.fillStyle = '#e8d4ae';
      ctx.beginPath();
      ctx.moveTo(fx + RAD, fy);
      ctx.lineTo(fx + fw - RAD, fy);
      ctx.quadraticCurveTo(fx + fw, fy, fx + fw, fy + RAD);
      ctx.lineTo(fx + fw, fy + fh - RAD);
      ctx.quadraticCurveTo(fx + fw, fy + fh, fx + fw - RAD, fy + fh);
      ctx.lineTo(fx + RAD, fy + fh);
      ctx.quadraticCurveTo(fx, fy + fh, fx, fy + fh - RAD);
      ctx.lineTo(fx, fy + RAD);
      ctx.quadraticCurveTo(fx, fy, fx + RAD, fy);
      ctx.closePath();
      ctx.fill();
      // Soft drop shadow underneath, drawn as a faint stroke so the
      // dialog feels lifted off the page without a hard edge.
      ctx.globalAlpha = fillAlpha * 0.25;
      ctx.strokeStyle = 'rgba(60, 40, 20, 0.5)';
      ctx.lineWidth = 0.8;
      ctx.stroke();
      ctx.restore();
    }

    // 1. Polygon frame border (no fill)
    if (this._framePoly && phases.frameT > 0) {
      ctx.globalAlpha = Math.min(1, phases.frameT * 2);
      this._drawPolyFrame(ctx, ox, oy);
      ctx.globalAlpha = 1;
    }

    // 1b. Handler bar — close X (top-right) and move handle (top-left)
    const b = this._bbox;
    const HR = 5;  // handler half-size (larger than corner squares which are 3)

    // Close button — top-right: square with X inside
    if (phases.frameT > 0.5) {
      const xAlpha = Math.min(1, (phases.frameT - 0.5) * 4);
      const bx = ox + b.x + b.w - 10;
      const by = oy + b.y + 10;
      ctx.globalAlpha = xAlpha * 0.6;
      ctx.strokeStyle = '#5a3820';
      ctx.lineWidth = 0.8;
      ctx.strokeRect(bx - HR, by - HR, HR * 2, HR * 2);
      ctx.lineWidth = 1.4;
      ctx.beginPath();
      ctx.moveTo(bx - 3, by - 3);
      ctx.lineTo(bx + 3, by + 3);
      ctx.moveTo(bx + 3, by - 3);
      ctx.lineTo(bx - 3, by + 3);
      ctx.stroke();
      ctx.globalAlpha = 1;
    }

    // Move handle — top-left: filled square when dragging, empty when idle
    if (phases.frameT > 0.6) {
      const hAlpha = Math.min(1, (phases.frameT - 0.6) * 3);
      const hx = ox + b.x + 10;
      const hy = oy + b.y + 10;
      ctx.globalAlpha = hAlpha * 0.55;
      ctx.strokeStyle = '#5a3820';
      ctx.lineWidth = 0.8;
      if (this.dragging) {
        ctx.fillStyle = '#5a3820';
        ctx.fillRect(hx - HR, hy - HR, HR * 2, HR * 2);
      } else {
        ctx.strokeRect(hx - HR, hy - HR, HR * 2, HR * 2);
      }
      ctx.globalAlpha = 1;
    }

    // 2. Content — options appear one by one as frameT finishes
    if (phases.contentT > 0) {
      const total = this._layouts.length;
      for (let i = 0; i < total; i++) {
        const lay = this._layouts[i];
        const opt = this._cfg.options[lay.idx];
        const itemT = Math.min(1, Math.max(0, (phases.contentT * total - i) / 1));
        if (itemT <= 0) continue;

        const sel = this._selected.get(opt.group) === lay.idx;

        ctx.save();
        ctx.translate(ox + lay.x + lay.w / 2, oy + lay.y + lay.h / 2);
        ctx.rotate(lay.wobble);

        // Shine on appear
        const elementAlpha = itemT;
        const shineBoost = itemT < 0.3 ? (0.3 - itemT) / 0.3 * 0.4 : 0;

        ctx.globalAlpha = elementAlpha;

        // Swatch display mode
        if (opt.display === 'swatch' && opt.swatchColor) {
          const r = opt.swatchRadius ?? 8;
          if (r > 0) {
            ctx.fillStyle = opt.swatchColor;
            ctx.beginPath();
            ctx.arc(0, -2, r, 0, Math.PI * 2);
            ctx.fill();
            ctx.font = '8px serif';
            ctx.fillStyle = '#5a3820';
            ctx.textAlign = 'center';
            ctx.textBaseline = 'top';
            ctx.fillText(opt.label, 0, r + 2);
          } else {
            // Text-only grid cell (no circle)
            ctx.font = sel ? 'bold 13px Georgia, serif' : '13px Georgia, serif';
            ctx.fillStyle = sel ? '#2a0e04' : '#5a3820';
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillText(opt.label, 0, 0);
          }
        } else if (opt.display === 'icon-only' && opt.icon) {
          // Icon + small label below
          ctx.font = '18px serif';
          ctx.fillStyle = sel ? '#2a0e04' : '#5a3820';
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.fillText(opt.icon, 0, -4);
          ctx.font = '7px serif';
          ctx.globalAlpha = elementAlpha * 0.6;
          ctx.fillText(opt.label, 0, 12);
        } else if (opt.display === 'inline-toggles' && opt.subToggles) {
          // Styled character toggle buttons (B bold, I italic, U underlined)
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          ctx.globalAlpha = elementAlpha;
          let tx = -lay.w / 2 + 2;
          for (const sub of opt.subToggles) {
            const charCx = tx + 12;
            // Pick font matching the style the character represents
            if (sub.key === 'bold') ctx.font = 'bold 14px Georgia, serif';
            else if (sub.key === 'italic') ctx.font = 'italic 14px Georgia, serif';
            else ctx.font = '14px Georgia, serif';
            ctx.fillStyle = sub.toggled ? '#2a0e04' : '#8a7060';
            ctx.fillText(sub.label, charCx, 0);
            // Draw underline for U
            if (sub.key === 'underline') {
              const uw = ctx.measureText(sub.label).width;
              ctx.strokeStyle = sub.toggled ? '#2a0e04' : '#8a7060';
              ctx.lineWidth = 1;
              ctx.beginPath();
              ctx.moveTo(charCx - uw / 2, 7);
              ctx.lineTo(charCx + uw / 2, 7);
              ctx.stroke();
            }
            // Border rectangle when toggled
            if (sub.toggled) {
              ctx.strokeStyle = '#5a3820';
              ctx.lineWidth = 0.8;
              ctx.strokeRect(charCx - 8, -9, 16, 18);
            }
            tx += 24;
          }
        } else if (opt.isToggle) {
          // Checkbox style
          const bx = -lay.w / 2 + 2;
          const by = -6;
          ctx.strokeStyle = '#5a3820';
          ctx.lineWidth = 0.8;
          ctx.globalAlpha = elementAlpha * 0.7;
          ctx.strokeRect(bx, by, 12, 12);
          if (opt.toggled) {
            ctx.strokeStyle = '#4a1a0a';
            ctx.lineWidth = 1.5;
            ctx.beginPath();
            ctx.moveTo(bx + 2, by + 6);
            ctx.lineTo(bx + 5, by + 10);
            ctx.lineTo(bx + 10, by + 2);
            ctx.stroke();
          }
          ctx.font = 'italic 13px Georgia, serif';
          ctx.fillStyle = opt.toggled ? '#2a0e04' : '#5a3820';
          ctx.textAlign = 'left';
          ctx.textBaseline = 'middle';
          ctx.globalAlpha = elementAlpha;
          ctx.fillText(opt.label, bx + 16, 0);
        } else {
          // Normal text label
          ctx.font = sel ? 'bold italic 13px Georgia, serif'
                        : 'italic 13px Georgia, serif';
          ctx.fillStyle = sel ? '#2a0e04' : '#5a3820';
          ctx.textAlign = 'center';
          ctx.textBaseline = 'middle';
          if (opt.icon) {
            ctx.fillText(opt.icon + ' ' + opt.label, 0, 0);
          } else {
            ctx.fillText(opt.label, 0, 0);
          }
        }

        // Shine glow overlay
        if (shineBoost > 0) {
          ctx.globalAlpha = shineBoost;
          ctx.shadowColor = this._frameAnim!.shine.glowColor;
          ctx.shadowBlur = 10;
          ctx.fillStyle = this._frameAnim!.shine.glowColor;
          ctx.beginPath();
          ctx.arc(0, 0, 4, 0, Math.PI * 2);
          ctx.fill();
          ctx.shadowBlur = 0;
        }

        ctx.restore();
      }
    }

    // 3. Selection wonky circles (resting + morph + cross-appear)
    this._selectionWidget.draw(ctx, ox, oy);

    ctx.restore();
  }

  // ── Polygon frame rendering ─────────────────────────────────────────────

  /** Render the polygon frame (edges, trails, corner squares, drag circle). */
  private _drawPolyFrame(ctx: CanvasRenderingContext2D, ox: number, oy: number): void {
    if (!this._framePoly) return;
    const snap = this._framePoly.snapshot();
    const GRAPHITE = '#5a3820';
    const TRAIL_COL = 'rgba(90,56,32,0.5)';

    // Edges — barely visible baseline (particles/trails are the main visual boundary)
    ctx.save();
    ctx.strokeStyle = GRAPHITE;
    ctx.lineWidth = 0.5;
    ctx.globalAlpha = (ctx.globalAlpha ?? 1) * 0.09;
    for (const edge of snap.edges) {
      const na = snap.nodes[edge.a];
      const nb = snap.nodes[edge.b];
      ctx.beginPath();
      ctx.moveTo(ox + na.x, oy + na.y);
      ctx.lineTo(ox + nb.x, oy + nb.y);
      ctx.stroke();
    }
    ctx.restore();

    // Trails — gradient lines like selection_polygon
    ctx.save();
    for (const trail of snap.trails) {
      if (trail.alpha <= 0.01) continue;
      const edge = snap.edges[trail.edgeIdx];
      const na = snap.nodes[edge.a];
      const nb = snap.nodes[edge.b];
      const fromNode = trail.direction === 1 ? na : nb;
      const toNode = trail.direction === 1 ? nb : na;
      const headX = ox + fromNode.x + (toNode.x - fromNode.x) * trail.head;
      const headY = oy + fromNode.y + (toNode.y - fromNode.y) * trail.head;
      const tailX = ox + fromNode.x + (toNode.x - fromNode.x) * trail.tail;
      const tailY = oy + fromNode.y + (toNode.y - fromNode.y) * trail.tail;
      const grad = ctx.createLinearGradient(tailX, tailY, headX, headY);
      grad.addColorStop(0, 'rgba(90,56,32,0)');
      grad.addColorStop(1, TRAIL_COL);
      ctx.strokeStyle = grad;
      ctx.lineWidth = 1.2;
      ctx.globalAlpha = trail.alpha;
      ctx.beginPath();
      ctx.moveTo(tailX, tailY);
      ctx.lineTo(headX, headY);
      ctx.stroke();
    }
    ctx.restore();

    // Corner and separator nodes — non-tilted (axis-aligned) squares
    ctx.save();
    const SQ_R = 3;
    const SEP_R = 2;  // separator nodes are slightly smaller
    for (let i = 0; i < snap.nodes.length; i++) {
      const n = snap.nodes[i];
      const occupied = snap.occupied.has(i);
      const nx = ox + n.x;
      const ny = oy + n.y;
      const r = i < 4 ? SQ_R : SEP_R;
      if (occupied) {
        ctx.fillStyle = '#3a2010';
        ctx.fillRect(nx - r, ny - r, r * 2, r * 2);
      }
      ctx.strokeStyle = GRAPHITE;
      ctx.lineWidth = i < 4 ? 0.8 : 0.6;
      ctx.strokeRect(nx - r, ny - r, r * 2, r * 2);
    }
    ctx.restore();

    // Particles — small dots when in transit
    ctx.save();
    for (const pv of snap.particles) {
      if (pv.resting) continue;
      ctx.fillStyle = '#3a2010';
      ctx.globalAlpha = 0.6;
      ctx.beginPath();
      ctx.arc(ox + pv.x, oy + pv.y, 1.5, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.restore();
  }

  // ── Internals ──────────────────────────────────────────────────────────

  /** Push current layout positions into the SelectionWidget. The `kind`
   *  drives the per-item selection animation (button press, checkbox tick,
   *  swatch pulse, icon glow). */
  private _syncSelectionWidgetItems(): void {
    if (!this._cfg) return;
    const items: WidgetItem[] = [];
    for (const lay of this._layouts) {
      const opt = this._cfg.options[lay.idx];
      items.push({
        cx: lay.x + lay.w / 2,
        cy: lay.y + lay.h / 2,
        r: Math.min(lay.w, lay.h) / 2 - 2,
        group: opt.group,
        value: opt.value,
        kind: kindForOption(opt),
      });
    }
    this._selectionWidget.updateItems(items);
  }

  private _buildLayout(): void {
    if (!this._cfg) return;
    const opts = this._cfg.options;

    // Group options by group number
    const groups = new Map<number, number[]>();
    for (let i = 0; i < opts.length; i++) {
      const g = opts[i].group;
      if (!groups.has(g)) groups.set(g, []);
      groups.get(g)!.push(i);
    }

    const lineH = 26;
    const charW = 7.5;
    const padX = 14;
    const padY = 12;
    const rowGap = 6;
    const inlineGap = 6;

    const ax = this._cfg.anchorX;
    const ay = this._cfg.anchorY;

    // Collect measured groups
    interface MeasuredGroup {
      indices: number[];
      isGrid: boolean;
      cols: number;
      rows: number;
      cellW: number;
      cellH: number;
      totalW: number;
      totalH: number;
    }
    const sortedGroups = [...groups.entries()].sort((a, b) => a[0] - b[0]);
    const measured: MeasuredGroup[] = [];

    for (const [, indices] of sortedGroups) {
      const firstOpt = opts[indices[0]];
      const isGrid = firstOpt.display === 'swatch' || firstOpt.display === 'icon-only';

      if (isGrid) {
        let cellW: number;
        if (firstOpt.display === 'swatch' && (firstOpt.swatchRadius ?? 8) === 0) {
          // Text-only grid: auto-size cells to widest label
          cellW = 0;
          for (const idx of indices) {
            const lw = opts[idx].label.length * 8 + 10;
            if (lw > cellW) cellW = lw;
          }
        } else {
          cellW = firstOpt.display === 'swatch' ? 28 : 36;
        }
        const cellH = firstOpt.display === 'swatch' ? 30 : 34;
        const cols = Math.min(indices.length, 6);
        const rows = Math.ceil(indices.length / cols);
        measured.push({ indices, isGrid: true, cols, rows, cellW, cellH, totalW: cols * cellW, totalH: rows * cellH });
      } else {
        let gMaxW = 0;
        for (const idx of indices) {
          let w: number;
          if (opts[idx].display === 'inline-toggles' && opts[idx].subToggles) {
            // 24px per styled character cell
            w = (opts[idx].subToggles!.length) * 24 + 4;
          } else {
            w = (opts[idx].icon ? 3 : 0) + opts[idx].label.length * charW + (opts[idx].isToggle ? 20 : 0);
          }
          if (w > gMaxW) gMaxW = w;
        }
        const th = indices.length * lineH;
        measured.push({ indices, isGrid: false, cols: 1, rows: indices.length, cellW: gMaxW, cellH: lineH, totalW: gMaxW, totalH: th });
      }
    }

    // Arrange groups into rows.
    // If any option specifies an explicit row, group by that; otherwise split at midpoint.
    const hasExplicitRows = sortedGroups.some(([, indices]) => opts[indices[0]].row != null);
    const rows: MeasuredGroup[][] = [];
    if (hasExplicitRows) {
      const rowMap = new Map<number, MeasuredGroup[]>();
      for (let i = 0; i < sortedGroups.length; i++) {
        const rowNum = opts[sortedGroups[i][1][0]].row ?? i;
        if (!rowMap.has(rowNum)) rowMap.set(rowNum, []);
        rowMap.get(rowNum)!.push(measured[i]);
      }
      for (const [, gs] of [...rowMap.entries()].sort((a, b) => a[0] - b[0])) {
        rows.push(gs);
      }
    } else {
      const midpoint = Math.ceil(measured.length / 2);
      if (measured.slice(0, midpoint).length > 0) rows.push(measured.slice(0, midpoint));
      if (measured.slice(midpoint).length > 0) rows.push(measured.slice(midpoint));
    }

    // Measure total dimensions
    let totalW = 0, totalH = 0;
    const rowWidths: number[] = [];
    const rowHeights: number[] = [];
    for (const row of rows) {
      let rw = 0, rh = 0;
      for (let i = 0; i < row.length; i++) {
        if (i > 0) rw += inlineGap;
        rw += row[i].totalW;
        if (row[i].totalH > rh) rh = row[i].totalH;
      }
      rowWidths.push(rw);
      rowHeights.push(rh);
      if (rw > totalW) totalW = rw;
      totalH += rh;
    }
    if (rows.length > 1) totalH += (rows.length - 1) * rowGap;
    totalW = Math.max(totalW, 80);

    // Compute anchor position
    let anchor = this._cfg.anchor;
    const pw = this._cfg.pageWidth ?? Infinity;
    const ph = this._cfg.pageHeight ?? Infinity;
    const bboxH = totalH + padY * 2;

    if (anchor.includes('left') && ax + padX + totalW + 20 > pw) {
      anchor = anchor.replace('left', 'right') as AnchorCorner;
    } else if (anchor.includes('right') && ax - totalW - padX - 20 < 0) {
      anchor = anchor.replace('right', 'left') as AnchorCorner;
    }
    if (anchor.includes('bottom') && ay - bboxH - 10 < 0) {
      anchor = anchor.replace('bottom', 'top') as AnchorCorner;
    } else if (anchor.includes('top') && ay + bboxH + 10 > ph) {
      anchor = anchor.replace('top', 'bottom') as AnchorCorner;
    }

    let sx: number, sy: number;
    if (anchor === 'bottom-right') {
      sx = ax - totalW - padX;
      sy = ay - totalH - padY;
    } else if (anchor === 'top-left') {
      sx = ax + padX;
      sy = ay + padY;
    } else if (anchor === 'top-right') {
      sx = ax - totalW - padX;
      sy = ay + padY;
    } else {
      sx = ax + padX;
      sy = ay - totalH - padY;
    }

    // Clamp
    if (pw < Infinity) {
      if (sx - padX < 0) sx = padX;
      if (sx + totalW + padX > pw) sx = pw - totalW - padX;
    }
    if (ph < Infinity) {
      if (sy - padY < 0) sy = padY;
      if (sy + totalH + padY > ph) sy = ph - totalH - padY;
    }

    // Assign positions — row by row, groups side by side
    this._layouts = [];
    this._separatorYs = [];
    let curY = sy;
    let revealIdx = 0;

    for (let ri = 0; ri < rows.length; ri++) {
      const row = rows[ri];
      let curX = sx;
      const rh = rowHeights[ri];

      for (let gi = 0; gi < row.length; gi++) {
        const gl = row[gi];
        if (gi > 0) curX += inlineGap;

        if (gl.isGrid) {
          for (let i = 0; i < gl.indices.length; i++) {
            const col = i % gl.cols;
            const r = Math.floor(i / gl.cols);
            this._layouts.push({
              x: curX + col * gl.cellW,
              y: curY + r * gl.cellH,
              w: gl.cellW,
              h: gl.cellH,
              wobble: (Math.random() - 0.5) * 0.02,
              idx: gl.indices[i],
              revealDelay: revealIdx * 30,
            });
            revealIdx++;
          }
        } else {
          for (let i = 0; i < gl.indices.length; i++) {
            this._layouts.push({
              x: curX,
              y: curY + i * gl.cellH,
              w: gl.totalW,
              h: gl.cellH,
              wobble: (Math.random() - 0.5) * 0.03,
              idx: gl.indices[i],
              revealDelay: revealIdx * 30,
            });
            revealIdx++;
          }
        }
        curX += gl.totalW;
      }

      // Record separator midpoint between this row and the next
      if (ri < rows.length - 1) {
        this._separatorYs.push(curY + rh + rowGap / 2);
      }
      curY += rh + rowGap;
    }

    this._bbox = {
      x: sx - padX,
      y: sy - padY,
      w: totalW + padX * 2,
      h: totalH + padY * 2,
    };
  }

  /** Build a Polygon rectangle frame around the current bbox, with separator nodes. */
  private _buildFramePoly(): void {
    const b = this._bbox;
    const m = 6; // margin outside bbox
    const x0 = b.x - m, y0 = b.y - m;
    const x1 = b.x + b.w + m, y1 = b.y + b.h + m;

    // 4 corner nodes
    const nodes: PolyNode[] = [
      { x: x0, y: y0 },   // 0: top-left (move handle)
      { x: x1, y: y0 },   // 1: top-right (close X)
      { x: x1, y: y1 },   // 2: bottom-right
      { x: x0, y: y1 },   // 3: bottom-left
    ];

    // Build edges: top stays intact, bottom stays intact.
    // Right edge (1→2) and left edge (3→0) get split by separator nodes.
    const edges: PolyEdge[] = [
      { a: 0, b: 1 },  // top
    ];

    // Add separator nodes on right and left edges (sorted top-to-bottom)
    const seps = this._separatorYs;
    let prevRight = 1;  // last node on the right edge going downward
    let leftNodes: number[] = [];  // left-side separator node indices (top to bottom)

    for (const sy of seps) {
      const rIdx = nodes.length;
      nodes.push({ x: x1, y: sy });  // right separator
      const lIdx = nodes.length;
      nodes.push({ x: x0, y: sy });  // left separator
      edges.push({ a: prevRight, b: rIdx });  // right edge segment down
      prevRight = rIdx;
      leftNodes.push(lIdx);
    }

    // Close right edge to bottom-right, then bottom, then left edge back up
    edges.push({ a: prevRight, b: 2 });  // right → bottom-right corner
    edges.push({ a: 2, b: 3 });           // bottom

    // Left edge: 3 → separators (bottom-to-top) → 0
    let prevLeft = 3;
    for (let i = leftNodes.length - 1; i >= 0; i--) {
      edges.push({ a: prevLeft, b: leftNodes[i] });
      prevLeft = leftNodes[i];
    }
    edges.push({ a: prevLeft, b: 0 });  // → top-left corner

    // Connect left–right separator pairs as cross edges
    for (let i = 0; i < seps.length; i++) {
      const rIdx = 4 + i * 2;
      const lIdx = 4 + i * 2 + 1;
      edges.push({ a: rIdx, b: lIdx });
    }

    if (this._framePoly) {
      this._framePoly.setGraph(nodes, edges);
    } else {
      this._framePoly = new Polygon(nodes, edges, {
        particleCount: 3,
        jumpProbability: 0.018,
        jumpDurationRange: [100, 220],
        cooldownRange: [500, 1400],
        trailFadeMs: 400,
      });
    }
  }

  /** Recompute node positions only (same topology) — used during drag. */
  private _updateFramePolyNodes(): void {
    if (!this._framePoly) return;
    const b = this._bbox;
    const m = 6;
    const x0 = b.x - m, y0 = b.y - m;
    const x1 = b.x + b.w + m, y1 = b.y + b.h + m;
    const nodes: PolyNode[] = [
      { x: x0, y: y0 },
      { x: x1, y: y0 },
      { x: x1, y: y1 },
      { x: x0, y: y1 },
    ];
    for (const sy of this._separatorYs) {
      nodes.push({ x: x1, y: sy });
      nodes.push({ x: x0, y: sy });
    }
    this._framePoly.updateNodes(nodes);
  }

  private _resolve(): void {
    if (!this._cfg) return;
    const result = new Map<number, PopOption>();
    for (const [group, idx] of this._selected) {
      result.set(group, this._cfg.options[idx]);
    }
    const cb = this._cfg.onResolve;
    this._cfg = null;
    this._frameAnim = null;
    this._framePoly = null;
    cb(result);
  }
}
